import { Alert } from "@package/subframe/components/Alert";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { FeatherX } from "@subframe/core";
import { format } from "date-fns";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ACK_COPY } from "@/lib/acknowledgements/copy";

interface ConfirmSignAcknowledgementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemTitle: string;
  itemTypeLabel: string;
  version: number;
  dueAt: string | Date | null;
  isSaving: boolean;
  onConfirm: () => void;
}

/**
 * The last step before a permanent record exists (AC12).
 *
 * Cloned from ConfirmAcknowledgeChecklistDialog, including the behaviour that
 * matters most: Escape, the overlay and the X are all refused while saving. A
 * dismissal mid-write would leave the staff member unable to tell whether an
 * irreversible record had been created — and there is no undo to fall back on.
 */
export function ConfirmSignAcknowledgementDialog({
  open,
  onOpenChange,
  itemTitle,
  itemTypeLabel,
  version,
  dueAt,
  isSaving,
  onConfirm,
}: ConfirmSignAcknowledgementDialogProps) {
  const handleOpenChange = (next: boolean) => {
    if (!next && isSaving) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="p-0 sm:max-w-[576px]"
        showCloseButton={false}
        data-testid="ack-confirm-dialog"
      >
        <DialogTitle className="sr-only">{ACK_COPY.CONFIRM_TITLE}</DialogTitle>
        <div className="flex h-full w-full flex-col items-start bg-default-background">
          <div className="flex w-full flex-col items-start gap-4 overflow-auto px-8 py-6">
            <div className="flex w-full items-center justify-between py-2">
              <span
                className="text-heading-2 font-heading-2 text-primary-navy-800"
                aria-hidden="true"
              >
                {ACK_COPY.CONFIRM_TITLE}
              </span>
              <IconButton
                variant="neutral-tertiary"
                icon={<FeatherX />}
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              />
            </div>

            <div className="flex w-full flex-col items-start gap-2 rounded-sm bg-neutral-50 px-4 py-4">
              <SummaryRow label="Document" value={itemTitle} />
              <SummaryRow label="Type" value={itemTypeLabel} />
              <SummaryRow label="Version" value={String(version)} />
              <SummaryRow
                label="Due"
                value={
                  dueAt ? format(new Date(dueAt), "d MMM yyyy") : "No due date"
                }
              />
            </div>

            <Alert
              variant="warning"
              title="This cannot be undone"
              description={ACK_COPY.CONFIRM_WARNING}
            />

            <div className="flex w-full items-center justify-end gap-2 pt-4">
              <Button
                variant="neutral-secondary"
                size="large"
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                {ACK_COPY.CONFIRM_CANCEL}
              </Button>
              <Button
                variant="brand-primary"
                size="large"
                onClick={onConfirm}
                disabled={isSaving}
                data-testid="ack-confirm-button"
              >
                {isSaving ? ACK_COPY.CONFIRM_SAVING : ACK_COPY.CONFIRM_ACTION}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex w-full items-start justify-between gap-4">
      <span className="text-caption font-caption text-subtext-color">
        {label}
      </span>
      <span className="text-caption font-caption text-default-font text-right">
        {value}
      </span>
    </div>
  );
}
